// Inline duplicate-finder panel for the Inventory screen. Sits between
// the Materials / Recipes tab body's toolbar and its list when the
// operator clicks "Find duplicates". Purely a presentation layer: the
// caller passes pre-computed groups + a row renderer.
//
// Why inline (not a side drawer or modal)?
//   - The Inventory screen's left column is already the natural "list
//     of materials/recipes" surface, so duplicates are a temporary
//     replacement of that list, not a parallel surface.
//   - A modal would steal the entire viewport for a cleanup task that
//     usually only needs the row text + a couple of buttons per row.
//   - A right-side drawer would compete with the existing right column
//     (posting forms) for attention.
//
// The component is generic over the item type so materials + recipes
// share one component; the parent supplies a renderRow callback that
// renders the per-row affordances (Edit + Deactivate for materials,
// Rename + Deactivate for recipes).
import { useMemo, useState, type ReactNode } from "react";
import {
  findDuplicateGroups,
  type DuplicateGroup,
} from "@/lib/manufacturing/duplicateFinder";
import { InlineHelp } from "@/components/common/InlineHelp";

type Props<T> = {
  /** The full source list (materials or recipes). The component groups
   *  internally on every render -- cheap because we're grouping at most
   *  a few thousand rows by a string key. */
  items: ReadonlyArray<T>;
  /** Extracts the primary description used for duplicate matching. */
  getName: (item: T) => string;
  /** React key for each item in a group. */
  getKey: (item: T) => string;
  /** Per-row render slot. The parent owns row-level affordances
   *  (Edit / Rename / Deactivate buttons + any inline state).
   *  `groupKey` is the normalized duplicate group key (for Keep selection). */
  renderRow: (item: T, indexInGroup: number, groupKey: string) => ReactNode;
  /** Closes the panel. */
  onClose: () => void;
  /** Plural noun for empty-state copy ("materials" or "recipes"). */
  entityPlural: string;
};

const closeBtnClass =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-800 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100 dark:hover:bg-zinc-700";

export function ManufacturingDuplicateFinderPanel<T>({
  items,
  getName,
  getKey,
  renderRow,
  onClose,
  entityPlural,
}: Props<T>) {
  const [search, setSearch] = useState("");
  const groups = useMemo(
    () => findDuplicateGroups(items, getName),
    [items, getName]
  );
  const filteredGroups = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups.filter((g) => g.label.toLowerCase().includes(q));
  }, [groups, search]);

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-950/20">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
            Duplicate {entityPlural}
          </h4>
          <span className="text-[11px] text-amber-700 dark:text-amber-300">
            {groups.length} group{groups.length === 1 ? "" : "s"} found
          </span>
          <InlineHelp
            label="How duplicate detection works"
            dismissKey="mfg.duplicates.detection"
          >
            We group {entityPlural} whose primary description matches after
            normalizing whitespace + case (so <em>"2x4 Stud"</em>,{" "}
            <em>"2X4  stud"</em>, and <em>"  2x4 stud "</em> are treated as
            one group). Only groups with 2+ entries are shown. False positives
            from typos (e.g. <em>"Plyood"</em> vs <em>"Plywood"</em>) are not
            caught in v1 -- they require fuzzy matching which can also produce
            false positives in a destructive workflow.
          </InlineHelp>
        </div>
        <button type="button" className={closeBtnClass} onClick={onClose}>
          Close
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="mt-3 text-xs text-amber-800 dark:text-amber-300">
          No duplicate {entityPlural} found.
        </div>
      ) : (
        <>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Filter groups by name…`}
            className="mt-3 w-full rounded-md border border-amber-200 bg-white px-2 py-1 text-xs text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 dark:border-amber-800 dark:bg-zinc-900 dark:text-zinc-100"
          />
          <ul className="mt-3 space-y-3">
            {filteredGroups.map((group) => (
              <DuplicateGroupBlock<T>
                key={group.key}
                group={group}
                groupKey={group.key}
                getKey={getKey}
                renderRow={renderRow}
              />
            ))}
          </ul>
          {filteredGroups.length === 0 ? (
            <div className="mt-3 text-xs text-amber-800 dark:text-amber-300">
              No groups match your filter.
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

function DuplicateGroupBlock<T>({
  group,
  groupKey,
  getKey,
  renderRow,
}: {
  group: DuplicateGroup<T>;
  groupKey: string;
  getKey: (item: T) => string;
  renderRow: (item: T, indexInGroup: number, groupKey: string) => ReactNode;
}) {
  return (
    <li className="rounded-lg border border-amber-100 bg-white p-2.5 dark:border-amber-900 dark:bg-zinc-900">
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-semibold text-gray-900 dark:text-zinc-100">
          {group.label}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-300">
          {group.items.length} entries
        </span>
      </div>
      <ul className="mt-2 divide-y divide-gray-100 dark:divide-zinc-700">
        {group.items.map((item, i) => (
          <li key={getKey(item)} className="py-1.5">
            {renderRow(item, i, groupKey)}
          </li>
        ))}
      </ul>
    </li>
  );
}
